import Phaser from 'phaser';
import { COLORS, UI, DEPTH } from '../constants';

export interface UIPanelConfig {
  x: number;
  y: number;
  width: number;
  height: number;
  title?: string;
  backgroundColor?: number;
  borderColor?: number;
  alpha?: number;
}

const TITLE_BAR_HEIGHT = 40;
const TITLE_BAR_INSET = 5;

export class UIPanel extends Phaser.GameObjects.Container {
  private background: Phaser.GameObjects.Rectangle;
  private border: Phaser.GameObjects.Rectangle;
  private titleBar: Phaser.GameObjects.Rectangle | null = null;
  private titleText: Phaser.GameObjects.Text | null = null;
  private panelWidth: number;
  private panelHeight: number;

  constructor(scene: Phaser.Scene, config: UIPanelConfig) {
    super(scene, config.x, config.y);

    this.panelWidth = config.width;
    this.panelHeight = config.height;
    const bgColor = config.backgroundColor ?? COLORS.BG_MEDIUM;
    const borderColor = config.borderColor ?? COLORS.PRIMARY;
    const alpha = config.alpha ?? 0.95;

    // Background
    this.background = scene.add.rectangle(0, 0, this.panelWidth, this.panelHeight, bgColor, alpha);

    // Border
    this.border = scene.add.rectangle(0, 0, this.panelWidth, this.panelHeight);
    this.border.setStrokeStyle(3, borderColor);
    this.border.setFillStyle(0x000000, 0);

    this.add([this.background, this.border]);

    // Title bar (if provided)
    if (config.title) {
      this.titleBar = scene.add.rectangle(
        0,
        -this.panelHeight / 2 + TITLE_BAR_HEIGHT / 2 + TITLE_BAR_INSET,
        this.panelWidth - 10,
        TITLE_BAR_HEIGHT,
        COLORS.PRIMARY,
        0.9
      );

      this.titleText = scene.add.text(0, -this.panelHeight / 2 + TITLE_BAR_HEIGHT / 2 + TITLE_BAR_INSET, config.title, {
        fontSize: `${UI.FONT_SIZE_MEDIUM}px`,
        color: '#ffffff',
        fontStyle: 'bold',
      });
      this.titleText.setOrigin(0.5);

      this.add([this.titleBar, this.titleText]);
    }

    this.setSize(this.panelWidth, this.panelHeight);
    this.setDepth(DEPTH.UI_BACKGROUND);

    scene.add.existing(this);
  }

  /**
   * Resize the panel to fit dynamic content. Updates background, border, and title bar.
   */
  resize(width: number, height: number): void {
    this.panelWidth = width;
    this.panelHeight = height;
    this.background.setSize(width, height);
    this.border.setSize(width, height);
    this.setSize(width, height);
    if (this.titleBar) {
      this.titleBar.setPosition(0, -height / 2 + TITLE_BAR_HEIGHT / 2 + TITLE_BAR_INSET);
      this.titleBar.setSize(width - 10, TITLE_BAR_HEIGHT);
    }
    if (this.titleText) {
      this.titleText.setPosition(0, -height / 2 + TITLE_BAR_HEIGHT / 2 + TITLE_BAR_INSET);
    }
  }

  // Get content area bounds (accounting for title bar)
  getContentBounds(): { x: number; y: number; width: number; height: number } {
    const titleOffset = this.titleText ? 50 : 0;
    return {
      x: this.x - this.panelWidth / 2 + UI.PANEL_PADDING,
      y: this.y - this.panelHeight / 2 + UI.PANEL_PADDING + titleOffset,
      width: this.panelWidth - UI.PANEL_PADDING * 2,
      height: this.panelHeight - UI.PANEL_PADDING * 2 - titleOffset,
    };
  }

  setTitle(title: string): void {
    if (this.titleText) {
      this.titleText.setText(title);
    }
  }

  // Fade in animation
  fadeIn(duration: number = 300): void {
    this.setAlpha(0);
    this.scene.tweens.add({
      targets: this,
      alpha: 1,
      duration,
      ease: 'Power2',
    });
  }

  // Fade out animation
  fadeOut(duration: number = 300, onComplete?: () => void): void {
    this.scene.tweens.add({
      targets: this,
      alpha: 0,
      duration,
      ease: 'Power2',
      onComplete,
    });
  }

  // Scale in animation
  popIn(duration: number = 200): void {
    this.setScale(0.8);
    this.setAlpha(0);
    this.scene.tweens.add({
      targets: this,
      scale: 1,
      alpha: 1,
      duration,
      ease: 'Back.easeOut',
    });
  }
}
